import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WagmiProvider } from 'wagmi';
import { config } from './config/wagmi';
import Header from './components/Header';
import Footer from './components/Footer';
import VerifyBadge from './components/VerifyBadge';
import ProposalList from './components/ProposalList';
import DeFiMetricsCard from './components/DeFiMetricsCard';
import DaoOverview from './components/DaoOverview';
import SupportedDaos from './components/SupportedDaos';
import NetworkStatus from './components/NetworkStatus';
import DeploymentInfo from './components/DeploymentInfo';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
          <Header />
          
          <main className="container mx-auto px-4 py-8">
            {/* Hero Section */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-dominant mb-4">
                SafeDash
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                A readable, integrity-verifiable web dashboard for DAO proposals and DeFi metrics
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Pinned to IPFS</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>ENS Verified</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span>Live Snapshot Data</span>
                </div>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              {/* Left Column - 2/3 width */}
              <div className="lg:col-span-2 space-y-8">
                <DeFiMetricsCard />
                <ProposalList />
              </div>

              {/* Right Column - 1/3 width */}
              <div className="space-y-8">
                <VerifyBadge />
                
                {/* Real DAO Overview with live data */}
                <DaoOverview />

                {/* Network Status */}
                <NetworkStatus />

                {/* Quick Actions */}
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h3 className="text-xl font-bold text-dominant mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button 
                      onClick={() => {
                        // This would open a voting interface or scroll to proposals
                        document.getElementById('proposals-section')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="bg-secondary text-white p-3 rounded-lg hover:bg-dominant transition-colors flex items-center justify-center gap-2"
                    >
                      <i className="fas fa-vote-yea"></i>
                      Vote
                    </button>
                    <button 
                      onClick={() => {
                        // This would open a swap interface
                        window.open('https://app.uniswap.org/', '_blank');
                      }}
                      className="bg-accent text-white p-3 rounded-lg hover:bg-opacity-90 transition-colors flex items-center justify-center gap-2"
                    >
                      <i className="fas fa-exchange-alt"></i>
                      Swap
                    </button>
                    <button 
                      onClick={() => {
                        // This would open a staking interface
                        window.open('https://lido.fi/', '_blank');
                      }}
                      className="bg-gray-100 text-gray-700 p-3 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <i className="fas fa-hand-holding-usd"></i>
                      Stake
                    </button>
                    <button 
                      onClick={() => {
                        // This would open analytics
                        window.open('https://dune.com/', '_blank');
                      }}
                      className="bg-gray-100 text-gray-700 p-3 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <i className="fas fa-chart-line"></i>
                      Analytics
                    </button>
                  </div>
                  
                  {/* Additional Actions */}
                  <div className="grid grid-cols-1 gap-2 mt-4">
                    <button 
                      onClick={() => {
                        // This would open delegate voting
                        window.open('https://delegate.cash/', '_blank');
                      }}
                      className="bg-gray-50 text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <i className="fas fa-user-check"></i>
                      Delegate Voting
                    </button>
                    <button 
                      onClick={() => {
                        // This would open governance discussion
                        window.open('https://discord.com/invite/ethereum', '_blank');
                      }}
                      className="bg-gray-50 text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <i className="fas fa-comments"></i>
                      Governance Chat
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Supported DAOs with Real Data */}
              <SupportedDaos />

              {/* Security Features */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-dominant mb-4">Security Features</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <i className="fas fa-shield-check text-green-600"></i>
                    </div>
                    <div>
                      <p className="font-medium">IPFS Pinned Content</p>
                      <p className="text-sm text-gray-600">Immutable frontend deployment</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="fas fa-fingerprint text-blue-600"></i>
                    </div>
                    <div>
                      <p className="font-medium">ENS Verification</p>
                      <p className="text-sm text-gray-600">Content hash integrity check</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <i className="fas fa-signature text-purple-600"></i>
                    </div>
                    <div>
                      <p className="font-medium">Signed Releases</p>
                      <p className="text-sm text-gray-600">Cryptographically verified builds</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <i className="fas fa-eye text-orange-600"></i>
                    </div>
                    <div>
                      <p className="font-medium">Transparent Governance</p>
                      <p className="text-sm text-gray-600">All votes and proposals visible</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Deployment Info */}
            <DeploymentInfo />
          </main>
          
          <Footer />
        </div>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

export default App;